import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { AuthService } from 'app/core/authentication/services/auth.service';
import { RouterRequest } from 'app/core/browser/models/router-request.model';
import { BrowserService } from 'app/core/browser/services/browser.service';
import { Observable, Observer, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NavItemSecondary } from '../nav-item-secondary.model';
import { ReportsNavItem } from '../nav-items/reports-nav-items.model';

/**
 * Prevent unauthorized activating and loading of secondary nav routes
 * @class ReportsNavGuard
 */
@Injectable()
export class ReportsNavGuard implements CanActivate, OnDestroy {

  private readonly _subscriptionDestroyer: Subject<boolean> = new Subject<boolean>();

  private readonly _reportsNav: ReportsNavItem = new ReportsNavItem();

  constructor(private readonly _browserService: BrowserService, private readonly _authService: AuthService) {}

  public ngOnDestroy(): void {
    this._subscriptionDestroyer.next(true);
    this._subscriptionDestroyer.complete();
  }

  /**
   * True when reqested route is authorized, redirects to next route if not
   * @method canActivate
   */
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    // find the index of the navigation item being requested
    const requestedItemIndex: number = this._reportsNav.menuItems.findIndex((item: NavItemSecondary) => '/' + item.route === state.url);

    const requestedMenuItem: NavItemSecondary = this._reportsNav.menuItems[requestedItemIndex];

    // Subscribe to the auth service and verify that user had authorization to navigate to the requested item
    const observable: Observable<Readonly<boolean>> =
      this._authService.isLoggedInUserAuthorized(requestedMenuItem.scopes,
                                                 requestedMenuItem.scopeMatch);

    observable
    .pipe(
      takeUntil(this._subscriptionDestroyer))
    .subscribe((isAuthorized: boolean) => {
      if(!isAuthorized) {
        // Move down the list of menu items and attempt to navigate there
        if(requestedItemIndex < (this._reportsNav.menuItems.length - 1)) {
          this._browserService.to(new RouterRequest({ route: this._reportsNav.menuItems[requestedItemIndex + 1].route }));
          return Observable.create((observer: Observer<boolean>) => { observer.next(false); });
        }
        // If no other items just reject the request
        return Observable.create((observer: Observer<boolean>) => { observer.next(false); });
      }
    });

    return Observable.create((observer: Observer<boolean>) => { observer.next(true); });
  }
}
